import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Building2, ExternalLink } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import PageHeader from "../components/PageHeader";
import StatusBadge from "../components/StatusBadge";
import FormDialog from "../components/FormDialog";
import ConfirmDialog from "../components/ConfirmDialog";
import AtecoInput from "../components/AtecoInput";
import CommittenteSelect, { CommittenteBadge } from "../components/CommittenteSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { COMPANY_FIELDS, OPTIONS } from "../lib/entities";
import { useLookupOptions } from "../lib/lookups";
import { initials } from "../lib/format";

export default function Companies() {
  const nav = useNavigate();
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [classification, setClassification] = useState("all");
  const [country, setCountry] = useState("all");
  const [sector, setSector] = useState("all");
  const [ateco, setAteco] = useState("");
  const [committente, setCommittente] = useState("all");
  const countries = useLookupOptions("country"); const sectors = useLookupOptions("sector");
  const [edit, setEdit] = useState(null);
  const [create, setCreate] = useState(false);
  const [del, setDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (classification !== "all") params.classification = classification;
    if (country !== "all") params.country = country;
    if (sector !== "all") params.sector = sector;
    if (ateco) params.ateco = ateco;
    if (committente !== "all") params.committente = committente;
    const { data } = await api.get("/companies", { params });
    setRows(data);
    setLoading(false);
  }, [search, classification, country, sector, ateco, committente]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("crm-refresh", h);
    return () => window.removeEventListener("crm-refresh", h);
  }, [load]);

  const remove = async () => {
    await api.delete(`/companies/${del.id}`);
    toast.success("Azienda eliminata");
    setDel(null); load();
  };

  return (
    <div>
      <PageHeader title="Aziende" subtitle={`${rows.length} record`}>
        {can("write") && <Button data-testid="add-company-button" onClick={() => setCreate(true)} className="bg-navy-900 hover:bg-navy-700 text-white gap-1.5"><Plus className="w-4 h-4" /> Nuova Azienda</Button>}
      </PageHeader>

      <div className="flex flex-wrap gap-2.5 mb-4">
        <Input data-testid="company-search" placeholder="Cerca…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-full sm:w-64 h-10" />
        <CommittenteSelect mode="filter" testid="filter-committente" value={committente} onChange={setCommittente} />
        <FilterSelect testid="filter-classification" value={classification} onChange={setClassification} placeholder="Classificazione" options={OPTIONS.classification} />
        <FilterSelect testid="filter-country" value={country} onChange={setCountry} placeholder="Paese" options={countries} />
        <FilterSelect testid="filter-sector" value={sector} onChange={setSector} placeholder="Settore" options={sectors} />
        <AtecoInput testid="filter-ateco" value={ateco} onChange={setAteco} placeholder="ATECO (es. 25.62)" className="w-full sm:w-56" />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden table-scroll">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wider text-slate-400">
              <th className="px-4 py-3 font-medium">Azienda</th>
              <th className="px-4 py-3 font-medium">Settore</th>
              <th className="px-4 py-3 font-medium">Paese</th>
              <th className="px-4 py-3 font-medium">Classificazione</th>
              <th className="px-4 py-3 font-medium">Owner</th>
              <th className="px-4 py-3 font-medium text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Caricamento…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-14 text-center text-slate-400"><Building2 className="w-8 h-8 mx-auto mb-2 opacity-40" />Nessuna azienda trovata</td></tr>
            ) : rows.map((c) => (
              <tr key={c.id} data-testid={`company-row-${c.id}`} onClick={() => nav(`/aziende/${c.id}`)} className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-navy-900/5 flex items-center justify-center text-navy-900 font-heading font-semibold text-xs">{initials(c.legal_name)}</div>
                    <div>
                      <p className="font-medium text-slate-800 flex items-center gap-2">{c.legal_name}<CommittenteBadge value={c.committente} testid={`company-committente-${c.id}`} /></p>
                      {c.trade_name && <p className="text-xs text-slate-400">{c.trade_name}</p>}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-600">
                  {c.sector || "—"}
                  {c.subsector && <p data-testid={`company-subsector-${c.id}`} className="text-xs text-slate-500 mt-0.5">{c.subsector}</p>}
                  {c.ateco && <p data-testid={`company-ateco-${c.id}`} className="text-xs text-slate-400 mt-0.5 truncate max-w-[16rem]" title={c.ateco_label || ""}><span className="font-mono text-teal-700">{c.ateco}</span>{c.ateco_label && ` · ${c.ateco_label}`}</p>}
                </td>
                <td className="px-4 py-3 text-slate-600">{c.country || "—"}</td>
                <td className="px-4 py-3"><StatusBadge group="classification" value={c.classification} /></td>
                <td className="px-4 py-3">
                  {c.owner ? <span className="w-7 h-7 rounded-full inline-flex items-center justify-center text-white text-[10px] font-semibold" style={{ background: c.owner.avatar_color }}>{initials(c.owner.name)}</span> : "—"}
                </td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => nav(`/aziende/${c.id}`)} className="icon-btn p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><ExternalLink className="w-4 h-4" /></button>
                    {can("write") && <button data-testid={`edit-company-${c.id}`} onClick={() => setEdit(c)} className="icon-btn p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><Pencil className="w-4 h-4" /></button>}
                    {can("delete") && <button data-testid={`delete-company-${c.id}`} onClick={() => setDel(c)} className="icon-btn p-1.5 rounded-lg hover:bg-red-50 text-red-500"><Trash2 className="w-4 h-4" /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <FormDialog open={create} onOpenChange={setCreate} title="Nuova Azienda" fields={COMPANY_FIELDS} endpoint="/companies" onSaved={load} />
      {edit && <FormDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} title="Modifica Azienda" fields={COMPANY_FIELDS} initial={edit} endpoint={`/companies/${edit.id}`} method="put" onSaved={load} />}
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} onConfirm={remove} description={`Eliminare "${del?.legal_name}"? Contatti e dati collegati resteranno ma verranno scollegati.`} />
    </div>
  );
}

export function FilterSelect({ testid, value, onChange, placeholder, options }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="w-full sm:w-auto sm:min-w-[150px] h-10"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{placeholder}: Tutti</SelectItem>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
